import React from "react";
import { graphql, Link } from "gatsby";
import Img from "gatsby-image";
import { MDXRenderer } from "gatsby-plugin-mdx";
import { FormattedMessage, FormattedList } from "react-intl";
import { DateTime } from "luxon";
import { Disqus } from "gatsby-plugin-disqus";

import Layout from "@app/components/Layout";
import SEO from "@app/components/SEO";
import ShareButtons from "@app/components/ShareButtons";
import RichContent from "@app/components/RichContent";
import ArticlesGrid from "@app/components/ArticlesGrid";
import useBalancedText from "@app/components/utils/useBalancedText";
import AuthorDisplay from "@app/components/AuthorDisplay";
import AuthorShortDisplay from "@app/components/AuthorShortDisplay";
import ContainerBleed from "@app/components/ContainerBleed";
import languages from "../app/intl/languages";

import styles from "./post.module.css";

const RELATED_POSTS_COUNT = 3;

function scoreRelated(myself, post) {
  // We calculate the score by comparing the tags
  return Array.from(
    new Set([...myself.frontmatter.tags, ...post.frontmatter.tags]),
  ).length;
}

export default function PostTemplate({ data, pageContext, location }) {
  const titleRef = React.useRef();
  const subtitleRef = React.useRef();

  const { mdx, related, nextRelated, site } = data;

  const {
    body,
    timeToRead,
    fields: { langKey, slug, date, postPath },
    frontmatter: { title, authors, subtitle, tags, featuredImage },
  } = mdx;

  const disqusConfig = {
    url: `${site.siteMetadata.siteUrl}${postPath}`,
    identifier: postPath,
    title: title,
  };

  const filteredRelated = [
    nextRelated,
    ...related.nodes
      .filter(
        (node) =>
          node.fields.slug !== slug &&
          (!nextRelated || node.id !== nextRelated.id),
      )
      .sort((a, b) => scoreRelated(mdx, a) - scoreRelated(mdx, b)),
  ]
    .filter(Boolean)
    .slice(0, RELATED_POSTS_COUNT);

  useBalancedText(titleRef, subtitleRef);

  return (
    <Layout currentURL={location.href} langKey={langKey}>
      <SEO
        lang={langKey}
        title={title}
        description={subtitle}
        imageUrl={featuredImage.childImageSharp.original.src}
        dateISO={DateTime.fromFormat(date, "yyyy-MM-dd").toISO()}
        authors={authors}
        article={mdx}
      />

      <div className={styles.wrapperWidth}>
        <header className={styles.header}>
          <div className={styles.main}>
            {title && (
              <h1 ref={titleRef} className={styles.title}>
                {title}
              </h1>
            )}
            {subtitle && (
              <h3 ref={subtitleRef} className={styles.lead}>
                {subtitle}
              </h3>
            )}
          </div>

          {authors?.length > 1 && (
            <div className={styles.meta}>
              {authors?.map((author) => (
                <AuthorShortDisplay
                  key={author.id}
                  id={author.id}
                  className={styles.author}
                  image={author.image}
                  fullName={author.fullName}
                  role={author.role}
                />
              ))}
            </div>
          )}

          <div className={styles.meta}>
            {authors?.length === 1 &&
              authors?.map((author) => (
                <AuthorShortDisplay
                  key={author.id}
                  id={author.id}
                  className={styles.author}
                  image={author.image}
                  fullName={author.fullName}
                  role={author.role}
                />
              ))}

            <div className={styles.info}>
              <div className={styles.timeToRead}>
                <FormattedMessage
                  id="time-to-read-in-minutes"
                  values={{ minutes: timeToRead }}
                />
              </div>

              <div>
                <FormattedList
                  value={tags.map((tag) => (
                    <strong key={tag}>{tag}</strong>
                  ))}
                />
              </div>
            </div>

            <div className={styles.share}>
              <ShareButtons url={location.href} />
            </div>
          </div>
        </header>

        {pageContext.alternates &&
          Object.keys(pageContext.alternates).length > 0 && (
            <div className={styles.alternates}>
              <FormattedMessage
                id="this-article-is-available-also-in-alternates"
                values={{
                  alternates: (
                    <FormattedList
                      value={Object.entries(pageContext.alternates).map(
                        ([langKey, { path, title }]) => (
                          <Link key={langKey} to={path} title={title}>
                            {languages[langKey].label}
                          </Link>
                        ),
                      )}
                    />
                  ),
                }}
              />
            </div>
          )}

        <ContainerBleed>
          <Img fluid={featuredImage.childImageSharp.fluid} />
        </ContainerBleed>

        <RichContent tag="article">
          <MDXRenderer>{body}</MDXRenderer>
        </RichContent>

        <div className={styles.authorBottom}>
          <div className={styles.headline}>
            <FormattedMessage id="writtenBy" />
          </div>

          <div className={styles.wrapContainer}>
            {authors.map((author) => (
              <AuthorDisplay
                key={author.id}
                id={author.id}
                className={styles.authorBottomItem}
                image={author.image}
                fullName={author.fullName}
                role={author.role}
                bio={author.bio}
              />
            ))}
          </div>
        </div>
      </div>

      <Disqus config={disqusConfig} />

      {filteredRelated.length > 0 && (
        <>
          <h2>
            <FormattedMessage id="related-articles" />
          </h2>
          <ArticlesGrid
            articles={filteredRelated}
            langKey={langKey}
            withFeatured={false}
          />
        </>
      )}
    </Layout>
  );
}

export const pageQuery = graphql`
  query($postPath: String!, $langKey: String!, $nextRelated: String) {
    mdx(fields: { postPath: { eq: $postPath } }) {
      body
      timeToRead
      fields {
        date
        langKey
        slug
        postPath
      }
      frontmatter {
        title
        subtitle
        tags
        featuredImage {
          childImageSharp {
            fluid(maxWidth: 2400) {
              ...GatsbyImageSharpFluid_withWebp
            }
            original {
              src
            }
          }
        }
        authors {
          id
          fullName
          role {
            ...Translations
          }
          bio {
            ...Translations
          }
          image {
            childImageSharp {
              fixed(height: 40, width: 40) {
                ...GatsbyImageSharpFixed_withWebp
              }
              bigger: fixed(height: 80, width: 80) {
                ...GatsbyImageSharpFixed_withWebp
              }
            }
          }
        }
      }
    }

    nextRelated: mdx(fields: { postPath: { eq: $nextRelated } }) {
      id
      timeToRead
      frontmatter {
        title
        subtitle
        tags
        featuredImage {
          childImageSharp {
            fluid(maxWidth: 400) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }

        authors {
          id
          fullName
          role {
            ...Translations
          }
          image {
            childImageSharp {
              fixed(height: 40, width: 40) {
                ...GatsbyImageSharpFixed_withWebp
              }
            }
          }
        }
      }
      fields {
        slug
        date
        langKey
        postPath
      }
    }

    related: allMdx(
      filter: { fields: { date: {}, langKey: { eq: $langKey } } }
      sort: { fields: [fields___date], order: DESC }
    ) {
      nodes {
        id
        timeToRead
        frontmatter {
          title
          subtitle
          tags
          featuredImage {
            childImageSharp {
              fluid(maxWidth: 400) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
          authors {
            id
            fullName
            role {
              ...Translations
            }
            image {
              childImageSharp {
                fixed(height: 40, width: 40) {
                  ...GatsbyImageSharpFixed_withWebp
                }
              }
            }
          }
        }
        fields {
          slug
          date
          langKey
          postPath
        }
      }
    }

    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;
